import { yupResolver } from '@hookform/resolvers/yup';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, TextField } from '@/DesignSystem/Component';
import type { ICompleteProfileForm } from '@/Module/Auth/Component';
import { AuthRegisterModal } from '@/Module/Auth/Component';
import ReferTypeModal from '@/Module/Auth/Component/AuthReferType/ReferTypeModal';
import { LOGIN_ERRORS } from '@/Module/Auth/Constant';
import { useCompleteSteps } from '@/Module/Auth/Hook';
import type { IAuthRubikaNotRegistered } from '@/Module/Auth/Model/ApiModel';
import { authCheckRubikaTokenAction, authCheckShadTokenAction, authGetCategoryByGradeIdAction, authGetMyPersonAction, authLinkRubikaUserAction, authLoginVerifyOtpCodeAction, authSendOtpCodeAction, authUpdatePersonAction } from '@/Module/Auth/Store/Action';
import { authSetIsTransitioningAction, authSetLoggedInAction, setLoginModalIsOpen, setSectionWelcome } from '@/Module/Auth/Store/Slice/AuthSlice';
import type { IUseOTP } from '@/Module/Comment/Hook/useOTP';
import { LINKS } from '@/Module/Core/Constant';
import { enqueueError, secondToMS, yup } from '@/Module/Core/Helper';
import { CookieService } from '@/Module/Core/Service';
import { IconKeySquare, IconRefresh } from '@/Module/Icon';
import { useAppDispatch, useAppSelector } from '@/Module/Store/Hook';
import { getSubscriptionsV2UserAction } from '@/Module/Subscription/Store/Action';
interface IForm {
  otp: string;
}
interface IAuthConfirmForm {
  setShowConfirmOTP?: IUseOTP['setShowConfirmOTP'];
  isModal?: boolean;
}
const DEFAULT_TIME = 2 * 60;
const AuthConfirmForm: FC<IAuthConfirmForm> = ({
  setShowConfirmOTP,
  isModal
}) => {
  const router = useRouter();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const dispatch = useAppDispatch();
  const [seconds, setSeconds] = useState(DEFAULT_TIME);
  const [isOpenRefer, setIsOpenRefer] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [needCompleteProfile, setNeedCompleteProfile] = useState(false);
  const person = useAppSelector(state => state.auth.person);
  const section = useAppSelector(state => state.auth.section);
  const isLoading = useAppSelector(state => state.auth.isLoading);
  const isTransitioning = useAppSelector(state => state.auth.isTransitioning);
  const phoneNumber = useAppSelector(state => state.auth.phoneNumber) ?? router.query?.mobile as string;
  const regex = /^\/?subscription(?:\/\d+)?\/?$/;
  const progressWidth = seconds * 100 / DEFAULT_TIME;
  const onCompleteSteps = useCompleteSteps();
  const timerHandler = useRef<ReturnType<typeof setInterval> | null>(null);
  const isSubscription = regex.test(decodeURIComponent(router.query?.redirectUrl as string ?? ''));
  const schema = useMemo(() => {
    return yup.object({
      otp: yup.string().required('لطفاً کد تایید را وارد کنید!').matches(/^[0-9]+$/, 'کد تایید بایستی عدد باشد!').min(5, 'تعداد ارقام کد تایید صحیح نیست!').max(5, 'تعداد ارقام کد تایید صحیح نیست!')
    });
  }, []);
  const {
    control,
    handleSubmit,
    reset
  } = useForm<IForm>({
    resolver: yupResolver(schema)
  });
  const checkRubikaToken = (rubikaToken: string) => {
    dispatch(authCheckRubikaTokenAction({
      token: rubikaToken
    })).unwrap().then(rubikaData => {
      dispatch(authLinkRubikaUserAction({
        outerUserId: (rubikaData.value as IAuthRubikaNotRegistered).outerUserId
      }));
    }).catch(error => {
      if (error?.data?.responseDesc === 'InvalidToken') {
        enqueueError('اعتبار توکن شما به پایان رسیده است، لطفاً از برنامه خارج شده و مجدداً وارد شوید.');
      } else {
        enqueueError('مشکلی پیش آمده، لطفاً از برنامه خارج شده و مجدداً وارد شوید.');
      }
    });
  };
  const checkShadToken = (shadToken: string) => {
    dispatch(authCheckShadTokenAction({
      token: shadToken
    })).unwrap().catch(() => {
      enqueueError('مشکلی پیش آمده، لطفاً از برنامه خارج شده و مجدداً وارد شوید.');
    });
  };
  const closeModal = async () => {
    dispatch(setLoginModalIsOpen(false));
  };
  const onNeedCompleteProfile = () => {
    setNeedCompleteProfile(true);
    localStorage.setItem('step', '1');
    dispatch(setSectionWelcome(1));
  };
  const onRetryClick = () => {
    dispatch(authSendOtpCodeAction(phoneNumber)).unwrap().then(() => {
      enqueueSnackbar({
        variant: 'success',
        message: 'کد ورود با موفقیت ارسال شد.'
      });
      setSeconds(DEFAULT_TIME);
    });
  };
  const loginByOtp = async (otp: string) => {
    return dispatch(authLoginVerifyOtpCodeAction({
      phone_number: phoneNumber,
      verification_code: otp
    })).unwrap().then(async data => {
      CookieService.setAccessToken(data.access_token);
      CookieService.setRefreshToken(data.refresh_token);

      // Checking for rubika token
      const rubikaToken = CookieService.getRubikaToken();
      if (rubikaToken) checkRubikaToken(rubikaToken);
      // Checking for shad token
      const shadToken = CookieService.getShadToken();
      if (shadToken) checkShadToken(shadToken);
      dispatch(authSetLoggedInAction(true));
      await dispatch(getSubscriptionsV2UserAction(data.access_token));
      return dispatch(authGetMyPersonAction()).unwrap();
    }).then(async data => {
      await dispatch(authGetCategoryByGradeIdAction(data?.gradeId || 15));
      return data;
    }).catch(error => {
      enqueueError(LOGIN_ERRORS[error.data.error.code.toUpperCase()] || 'خطایی رخ داده است!');
    });
  };
  const onSubmit = handleSubmit(data => {
    loginByOtp(data.otp).then(loginData => {
      const {
        isComplete,
        userId
      } = loginData!;
      // If user is coming from a modal login, we need to close it.
      if (isModal) closeModal();
      // If user is coming from a subscription page or completed his profile,
      // we don't want to route him to welcome flow.
      if (isSubscription || isComplete) {
        onCompleteSteps({
          userId
        });
        return;
      }
      onNeedCompleteProfile();
    }).catch(() => {
      reset();
    });
  });
  const onRegisterSuccess = (payLoad: ICompleteProfileForm) => {
    dispatch(authUpdatePersonAction({
      id: person!.id,
      fundametalId: person?.fundametalId,
      gender: person?.gender,
      name: payLoad.firstName,
      family: payLoad.lastName,
      gradeId: payLoad.grad.value,
      fieldId: payLoad.field?.value,
      birthDate: payLoad.birthDate,
      stateId: payLoad.province?.value,
      cityId: payLoad.city?.value
    })).unwrap().then(newPerson => {
      if (typeof window !== 'undefined') {
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'sign_up',
            userId: newPerson?.userId,
            timestamp: new Date().toISOString()
          });
        }
      }
      if (isSubscription) {
        onCompleteSteps({
          userId: person!.id
        });
        return;
      }
      localStorage.setItem('step', '2');
      dispatch(setSectionWelcome(2));
      setNeedCompleteProfile(false);
      setShowRegisterModal(false);
      setIsOpenRefer(true);
    });
  };
  const onCompleteProfileSuccess = (payLoad: ICompleteProfileForm) => {
    dispatch(authUpdatePersonAction({
      id: person!.id,
      fundametalId: person?.fundametalId,
      gender: person?.gender,
      name: payLoad.firstName,
      family: payLoad.lastName,
      gradeId: payLoad.grad.value,
      fieldId: payLoad.field?.value,
      birthDate: payLoad.birthDate!,
      stateId: payLoad.province?.value,
      cityId: payLoad.city?.value
    })).unwrap().then(() => {
      setNeedCompleteProfile(false);
      localStorage.setItem('step', '2');
      dispatch(setSectionWelcome(2));
      setIsOpenRefer(true);
    });
  };
  dispatch(authSetIsTransitioningAction(false));
  useEffect(() => {
    if (timerHandler.current) clearInterval(timerHandler.current);
    if (seconds < 1) {
      return () => {};
    }
    timerHandler.current = setInterval(() => {
      setSeconds(prevState => prevState - 1);
    }, 1000);
    return () => {
      if (timerHandler.current) clearInterval(timerHandler.current);
    };
  }, [seconds]);
  useEffect(() => {
    if (section === 510) setIsOpenRefer(false);
  }, [section]);
  return <>
      <form onSubmit={onSubmit} className="mb-6 flex flex-1 flex-col">
        <p className="mb-4 text-sm ">
          <span className="text-blackThree">کد تأیید به شمارۀ</span>
          <span className="px-1 font-semibold text-blackFive">{phoneNumber} </span>
          <span className="text-blackThree">فرستاده شد. </span>
        </p>

        <TextField control={control} name="otp" type="tel" placeholder="کد تایید" prefix={<IconKeySquare />} className="mb-4" autoComplete="off" autoFocus data-sentry-element="TextField" data-sentry-source-file="AuthConfirmForm.tsx" />
        {!isModal ? <div className=" flex flex-1 flex-row items-end justify-center gap-2">
            <span className="mb-2">شماره همراه اشتباه است؟</span>
            <Button as={Link} variant="text" color="primary" size="small" href={LINKS.AUTH.LOGIN} className=" font-bold">
              تغییر شماره
            </Button>
          </div> : <div className="flex flex-row items-center justify-center gap-2">
            <span>شماره همراه اشتباه است؟</span>
            <Button variant="text" color="primary" size="small" className="font-bold" onClick={setShowConfirmOTP}>
              تغییر شماره
            </Button>
          </div>}
        <Button id="otp" bold disabled={isLoading || isTransitioning} onClick={() => dispatch(authSetIsTransitioningAction(true))} className="relative mt-4 w-full" variant="shadow" color="primary" radius="circle" size="large" type="submit" data-sentry-element="Button" data-sentry-source-file="AuthConfirmForm.tsx">
          تایید
        </Button>
      </form>

      <div className="relative">
        {seconds === 0 ? <div className="flex items-center justify-center border-b-8 border-redFour text-blueFour">
            <Button className="text-redFour" variant="text" afterIcon={<IconRefresh className="!w-auto !p-0 text-white" />} bold disabled={isLoading || isTransitioning} onClick={onRetryClick}>
              ارسال مجدد کد تایید
            </Button>
          </div> : <div className="flex cursor-wait items-center justify-center gap-1 border-b-8 border-redFour pb-4 text-center text-lg text-blackThree">
            <span className="font-bold text-redFour">{secondToMS(seconds)} دقیقه</span>
            مانده تا دریافت مجدد رمز
            <span style={{
          width: `${progressWidth}%`
        }} className="absolute bottom-0 left-0 h-2 bg-orangeThree transition-all" />
          </div>}
      </div>

      <AuthRegisterModal isRegister={showRegisterModal} isOpen={showRegisterModal || needCompleteProfile} onSuccess={needCompleteProfile ? onCompleteProfileSuccess : onRegisterSuccess} data-sentry-element="AuthRegisterModal" data-sentry-source-file="AuthConfirmForm.tsx" />

      <ReferTypeModal isOpenRefer={isOpenRefer} data-sentry-element="ReferTypeModal" data-sentry-source-file="AuthConfirmForm.tsx" />
    </>;
};
export default AuthConfirmForm;